import { useEffect, useState } from "react";
import { Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useProducts, cleanTitle } from "@/lib/api";
import { apiError, photoUrl } from "@/lib/reviews";
import { useBeforeAfter } from "@/components/shop/BeforeAfter";

const admin = axios.create({ baseURL: API, withCredentials: true });

const FilePick = ({ label, file, setFile, testid }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!file) return setUrl(null);
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return (
  <label className="flex-1 cursor-pointer">
    <span className="text-xs text-white/60">{label}</span>
    <div className="mt-1 aspect-[4/5] border border-dashed border-[#c9a55c]/50 flex items-center justify-center overflow-hidden hover:bg-[#c9a55c]/10">
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : <Upload size={20} className="text-gold" />}
    </div>
    <input data-testid={testid} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => setFile(e.target.files[0] || null)} />
  </label>
  );
};

export const BeforeAfterAdmin = () => {
  const { data: products = [] } = useProducts({});
  const { data: pairs = [], refetch } = useBeforeAfter();
  const [handle, setHandle] = useState("");
  const [caption, setCaption] = useState("");
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const title = (h) => cleanTitle(products.find((p) => p.handle === h)?.title || h);

  const submit = async (e) => {
    e.preventDefault();
    if (!handle || !before || !after) return toast.error("Odaberite proizvod i obje fotografije.");
    setBusy(true);
    const fd = new FormData();
    fd.append("product_handle", handle);
    fd.append("caption", caption);
    fd.append("before", before);
    fd.append("after", after);
    try {
      await admin.post("/admin/before-after", fd);
      toast.success("Prije/poslije objavljeno");
      setBefore(null); setAfter(null); setCaption(""); setFormKey((k) => k + 1);
      refetch();
    } catch (x) { toast.error(apiError(x)); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Obrisati ovaj prije/poslije par?")) return;
    try { await admin.delete(`/admin/before-after/${id}`); toast.success("Obrisano"); refetch(); } catch (x) { toast.error(apiError(x)); }
  };

  return (
    <section data-testid="admin-before-after" className="mt-14">
      <h2 className="font-display text-3xl mb-2">Prije i poslije</h2>
      <p className="text-sm text-white/50 mb-6">Fotografije se odmah prikazuju na stranici proizvoda. Objavljujte samo fotografije za koje imate dopuštenje kupca.</p>
      <form onSubmit={submit} className="border border-white/10 p-6 grid md:grid-cols-[1fr_1.2fr] gap-6">
        <div className="space-y-3">
          <select data-testid="admin-ba-product-select" value={handle} onChange={(e) => setHandle(e.target.value)} className="w-full h-11 bg-[#0e0d0b] border border-white/15 px-3 text-sm">
            <option value="">Odaberite proizvod…</option>
            {products.map((p) => <option key={p.handle} value={p.handle}>{cleanTitle(p.title)}</option>)}
          </select>
          <input data-testid="admin-ba-caption-input" value={caption} maxLength={200} onChange={(e) => setCaption(e.target.value)} placeholder="Opis (npr. nakon 4 tjedna)" className="w-full h-11 bg-transparent border border-white/15 px-3 text-sm" />
          <button data-testid="admin-ba-submit-button" disabled={busy} className="btn-gold h-11 px-6">{busy ? "Učitavanje…" : "Objavi"}</button>
        </div>
        <div className="flex gap-3" key={formKey}>
          <FilePick label="Prije" file={before} setFile={setBefore} testid="admin-ba-before-input" />
          <FilePick label="Poslije" file={after} setFile={setAfter} testid="admin-ba-after-input" />
        </div>
      </form>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {pairs.map((p) => (
          <div key={p.id} data-testid={`admin-ba-item-${p.id}`} className="border border-white/10 p-3">
            <div className="flex gap-2">
              <img src={photoUrl(p.before)} alt="" className="w-1/2 aspect-[4/5] object-cover" />
              <img src={photoUrl(p.after)} alt="" className="w-1/2 aspect-[4/5] object-cover" />
            </div>
            <div className="flex items-center justify-between mt-2 gap-2">
              <p className="text-xs text-white/70 truncate">{title(p.product_handle)}{p.caption && ` · ${p.caption}`}</p>
              <button data-testid={`admin-ba-delete-${p.id}`} onClick={() => remove(p.id)} className="h-8 w-8 border border-white/15 flex items-center justify-center hover:text-red-400" aria-label="Obriši"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
